import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoRefreshOutline } from "react-icons/io5";
import { useTranslation } from 'react-i18next';
import { getRecommendations } from '../utils/recommendationAPI';
import { fetchRecommendedPapers } from '../utils/fetchRecommendedpapers';
import { getJournalMetrics } from '../utils/journalData';
import RecommendedPDF from './RecommendedPDF';
import HorizontalShelf from './HorizontalShelf';

const RECOMMENDATION_BATCH_SIZE = 6;
const RECOMMENDATION_POOL_SIZE = 12;

const getFallbackRecencyBoost = (publicationDate = '') => {
  if (!publicationDate) {
    return 0.02;
  }

  const parsedDate = new Date(publicationDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return 0.02;
  }

  const ageInDays = (Date.now() - parsedDate.getTime()) / (1000 * 60 * 60 * 24);

  if (ageInDays <= 90) return 0.1;
  if (ageInDays <= 365) return 0.07;
  if (ageInDays <= 365 * 3) return 0.04;
  return 0.02;
};

const normalizeMetric = (value) => {
  if (!value || value === 'Unknown' || value === 'N/A') {
    return null;
  }

  return value;
};

const buildFallbackRecommendations = async (keywords = []) => {
  const fallbackKeywords = keywords.filter(Boolean).slice(0, 2);
  const fallbackPapers = [];

  for (let keywordIndex = 0; keywordIndex < fallbackKeywords.length; keywordIndex += 1) {
    const keyword = fallbackKeywords[keywordIndex];
    let papers = await fetchRecommendedPapers(keyword, 365);

    if (!papers.length) {
      papers = await fetchRecommendedPapers(keyword, 365 * 2);
    }

    papers.forEach((paper, paperIndex) => {
      fallbackPapers.push({
        ...paper,
        keyword,
        relevance_score: Math.max(
          0.48,
          0.88 - (keywordIndex * 0.1) - (paperIndex * 0.05) + getFallbackRecencyBoost(paper.PubDate)
        ),
        reasons: [
          `Matched keyword: ${keyword}`,
          'Interest-ranked fallback',
        ],
      });
    });
  }

  const uniquePapers = [];
  const seenPmids = new Set();

  fallbackPapers.forEach((paper) => {
    if (!paper.PMID || seenPmids.has(paper.PMID)) {
      return;
    }

    seenPmids.add(paper.PMID);
    uniquePapers.push(paper);
  });

  return uniquePapers.slice(0, 6);
};

const RecommendationSection = ({ recentPdfs, isDarkMode }) => {
  const { t, i18n } = useTranslation();
  const [recommendationPool, setRecommendationPool] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [batchStartIndex, setBatchStartIndex] = useState(0);
  const [profileMeta, setProfileMeta] = useState({
    basedOn: [],
    userProfile: {},
    searchSource: '',
  });

  const realRecentCount = useMemo(
    () => recentPdfs.filter((pdf) => pdf.id !== 'sample123').length,
    [recentPdfs]
  );

  const visibleRecommendations = useMemo(() => {
    if (recommendationPool.length <= RECOMMENDATION_BATCH_SIZE) {
      return recommendationPool;
    }

    const nextBatch = recommendationPool.slice(
      batchStartIndex,
      batchStartIndex + RECOMMENDATION_BATCH_SIZE
    );

    if (nextBatch.length === RECOMMENDATION_BATCH_SIZE) {
      return nextBatch;
    }

    return [
      ...nextBatch,
      ...recommendationPool.slice(0, RECOMMENDATION_BATCH_SIZE - nextBatch.length),
    ];
  }, [batchStartIndex, recommendationPool]);

  const fetchRecommendations = useCallback(async ({ forceRefresh = false, resetBatch = true } = {}) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Fetching personalized recommendations...');

      const result = await getRecommendations(RECOMMENDATION_POOL_SIZE, {
        recentPdfs,
        forceRefresh,
      });
      let papers = result.recommendations || [];

      if (papers.length === 0 && result.based_on?.length && recentPdfs.some((pdf) => pdf.id !== 'sample123')) {
        papers = await buildFallbackRecommendations(result.based_on);
      }

      const enrichedPapers = papers.map((paper) => {
        const journalName = paper.journal || paper.JT || '';
        const journalMetrics = getJournalMetrics(journalName);

        return {
          PMID: paper.pubmed_id || paper.PMID || '',
          TI: paper.title || paper.TI || '',
          JT: journalName,
          AU: paper.authors || paper.AU || '',
          AD: paper.institution || paper.AD || '',
          IF: normalizeMetric(journalMetrics?.if || paper.if_factor || paper.IF),
          Q: normalizeMetric(journalMetrics?.q || paper.jcrq || paper.Q),
          B: normalizeMetric(journalMetrics?.b || paper.cas_block || paper.B),
          PubDate: paper.publication_date || paper.PubDate || '',
          keyword: paper.category || paper.keyword || '',
          relevance_score: paper.relevance_score || 0,
          reasons: paper.reasons || [],
        };
      });

      setRecommendationPool(enrichedPapers);
      if (resetBatch) {
        setBatchStartIndex(0);
      }
      setProfileMeta({
        basedOn: result.based_on || [],
        userProfile: result.user_profile || {},
        searchSource: enrichedPapers.length > 0 && (result.recommendations || []).length === 0
          ? 'PubMed fallback'
          : (result.search_source || ''),
      });
      setError(enrichedPapers.length > 0 ? null : (result.error || null));
      setLastUpdate(new Date());

      console.log('✅ Recommendations loaded:', enrichedPapers.length);
    } catch (err) {
      console.error('❌ Error fetching recommendations:', err);
      setError('Unable to fetch personalized recommendations');
      setRecommendationPool([]);
    } finally {
      setLoading(false);
    }
  }, [recentPdfs]);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const handleRefreshBatch = useCallback(async () => {
    if (loading) {
      return;
    }

    if (recommendationPool.length > RECOMMENDATION_BATCH_SIZE) {
      setBatchStartIndex((previousIndex) => (
        (previousIndex + RECOMMENDATION_BATCH_SIZE) % recommendationPool.length
      ));
      return;
    }

    await fetchRecommendations({ forceRefresh: true, resetBatch: true });
  }, [fetchRecommendations, loading, recommendationPool.length]);

  const handlePubMedClick = (pmid) => {
    if (pmid) {
      window.open(`https://pubmed.ncbi.nlm.nih.gov/${pmid}/`, '_blank');
    }
  };

  const refreshLabel = i18n.language === 'zh' ? '刷新一批' : 'Refresh batch';

  return (
    <section
      className={`relative shrink-0 overflow-hidden rounded-[32px] border p-4 md:p-5 ${
        isDarkMode
          ? 'border-white/10 bg-[#081120]/85 shadow-[0_30px_90px_rgba(2,6,23,0.55)]'
          : 'border-slate-200/90 bg-white/90 shadow-[0_28px_90px_rgba(148,163,184,0.22)]'
      }`}
    >
      <div className={`absolute inset-0 ${
        isDarkMode
          ? 'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.20),transparent_30%),radial-gradient(circle_at_80%_20%,rgba(236,72,153,0.14),transparent_24%),linear-gradient(180deg,rgba(15,23,42,0.45),rgba(8,17,32,0.95))]'
          : 'bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,0.12),transparent_30%),radial-gradient(circle_at_80%_20%,rgba(236,72,153,0.10),transparent_24%),linear-gradient(180deg,rgba(248,250,252,0.78),rgba(255,255,255,0.92))]'
      }`} />

      <div className="relative">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] ${
              isDarkMode ? 'bg-cyan-400/10 text-cyan-200' : 'bg-cyan-50 text-cyan-700'
            }`}>
              {t('homeTab.paperRecommendationsHero', 'A paper feed tuned to your interests')}
            </div>

            <div className="flex flex-wrap items-center justify-end gap-2">
              {profileMeta.searchSource && recommendationPool.length > 0 && (
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium ${
                  isDarkMode ? 'bg-white/6 text-slate-300' : 'bg-white/75 text-slate-500'
                }`}>
                  {profileMeta.searchSource}
                </span>
              )}

              {lastUpdate && recommendationPool.length > 0 && (
                <span className={`inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium ${
                  isDarkMode ? 'bg-white/6 text-slate-300' : 'bg-white/75 text-slate-500'
                }`}>
                  {`${t('homeTab.updatedAt', 'Updated')} ${lastUpdate.toLocaleTimeString(i18n.language === 'zh' ? 'zh-CN' : 'en-US')}`}
                </span>
              )}

              <button
                onClick={handleRefreshBatch}
                disabled={loading}
                className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold transition-colors ${
                  isDarkMode
                    ? 'border-white/10 bg-white/5 text-slate-200 hover:bg-white/10'
                    : 'border-slate-200 bg-white/80 text-slate-700 hover:bg-slate-50'
                } disabled:opacity-50`}
                title={refreshLabel}
              >
                <IoRefreshOutline size={16} className={loading ? 'animate-spin' : ''} />
                {refreshLabel}
              </button>
            </div>
          </div>

          {error && (
            <div className={`mt-3 rounded-[20px] border px-4 py-3 text-sm ${
              isDarkMode
                ? 'border-amber-400/20 bg-amber-500/10 text-amber-100'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}>
              {t(
                'homeTab.recommendationSoftError',
                'Live PubMed sync is unavailable right now. Your reading profile is still ready for the next refresh.'
              )}
            </div>
          )}
        </div>

        <div className="mt-4">
          {loading && (
            <div>
              <div className={`mb-3 inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium ${
                isDarkMode ? 'bg-cyan-400/10 text-cyan-100' : 'bg-cyan-50 text-cyan-700'
              }`}>
                {t(
                  'homeTab.recommendationLoading',
                  'Matching your papers with related studies. Finding papers you may want to read...'
                )}
              </div>
              <HorizontalShelf isDarkMode={isDarkMode} step={560}>
                {[0, 1, 2, 3].map((item) => (
                  <div
                    key={item}
                    className={`h-[260px] w-[272px] shrink-0 snap-start rounded-[24px] border animate-pulse ${
                      isDarkMode ? 'border-white/8 bg-white/5' : 'border-slate-200 bg-slate-100'
                    }`}
                  />
                ))}
              </HorizontalShelf>
            </div>
          )}

          {!loading && recommendationPool.length === 0 && (
            <div className={`rounded-[24px] border p-5 md:p-6 ${
              isDarkMode ? 'border-white/8 bg-white/5 text-slate-200' : 'border-slate-200 bg-white/80 text-slate-700'
            }`}>
              <div className="max-w-2xl">
                <p className={`text-[11px] font-semibold uppercase tracking-[0.22em] ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  {t('homeTab.feedWarmup', 'Feed warm-up')}
                </p>
                <h3 className={`mt-2 text-xl font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}>
                  {realRecentCount > 0
                    ? t('homeTab.feedNeedsMoreSignal', 'Your feed is learning from the papers you have uploaded.')
                    : t('homeTab.feedNeedsUpload', 'Upload your first paper to unlock personalized recommendations.')}
                </h3>
                <p className={`mt-2 text-sm leading-6 ${
                  isDarkMode ? 'text-slate-300' : 'text-slate-600'
                }`}>
                  {realRecentCount > 0
                    ? t(
                        'homeTab.feedNeedsMoreSignalBody',
                        'The recommendation engine is ready. Add another paper or refresh to give it a wider signal set and generate stronger matches.'
                      )
                    : t(
                        'homeTab.feedNeedsUploadBody',
                        'We rank recommendations from what you actually read, not from one-size-fits-all trending lists. Start with a PDF on the left and this section will wake up.'
                      )}
                </p>
              </div>
            </div>
          )}

          {!loading && recommendationPool.length > 0 && (
            <>
              <HorizontalShelf isDarkMode={isDarkMode} step={560}>
                {visibleRecommendations.map((pdf, index) => (
                  <div key={`rec-shelf-${pdf.PMID}-${index}`} className="snap-start">
                    <RecommendedPDF
                      PMID={pdf.PMID}
                      journalName={pdf.JT}
                      title={pdf.TI}
                      authors={pdf.AU}
                      institution={pdf.AD}
                      metrics={{
                        if: pdf.IF,
                        casBlock: pdf.B,
                        jcrQ: pdf.Q
                      }}
                      keyword={pdf.keyword}
                      isDarkMode={isDarkMode}
                      onClick={() => handlePubMedClick(pdf.PMID)}
                      pubDate={pdf.PubDate}
                      matchScore={pdf.relevance_score}
                      reasons={pdf.reasons}
                      compact
                    />
                  </div>
                ))}
              </HorizontalShelf>

            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default RecommendationSection;
